/**
 * Webhook Test Harness
 * Test page to debug webhook and email issues
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'error' | 'success' | 'warning';
  message: string;
  step?: string;
}

export const WebhookTestHarness: React.FC = () => {
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [testResult, setTestResult] = useState<any>(null);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testUserId, setTestUserId] = useState('');
  const [authTestResult, setAuthTestResult] = useState<any>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addLog = (level: LogEntry['level'], message: string, step?: string) => {
    const logEntry: LogEntry = {
      timestamp: new Date().toLocaleTimeString(),
      level,
      message,
      step
    };
    setLogs(prev => [...prev, logEntry]);
  };

  const clearLogs = () => {
    setLogs([]);
    setTestResult(null);
  };

  // Generate real Stripe webhook signature using Web Crypto API
  const generateWebhookSignature = async (payload: string, secret: string): Promise<string> => {
    if (!secret || !secret.startsWith('whsec_')) {
      throw new Error('Invalid webhook secret format. Must start with "whsec_"');
    }

    const timestamp = Math.floor(Date.now() / 1000);
    const signedPayload = `${timestamp}.${payload}`;
    
    // Encode the secret and payload
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const payloadData = encoder.encode(signedPayload);
    
    // Import the key for HMAC
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    // Generate the signature
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, payloadData);
    
    // Convert signature to hex string
    const signatureHex = Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    return `t=${timestamp},v1=${signatureHex}`;
  };

  const testWebhook = async () => {
    setIsTestRunning(true);
    clearLogs();
    
    addLog('info', '🚀 Starting webhook test...', 'START');

    // Validate webhook secret first
    if (!webhookSecret || !webhookSecret.startsWith('whsec_')) {
      addLog('error', '❌ Please enter a valid webhook secret starting with "whsec_"', 'ERROR');
      setIsTestRunning(false);
      return;
    }

    addLog('info', `🔐 Using webhook secret: ${webhookSecret.substring(0, 15)}...`, 'SECRET');

    try {
      // Step 1: Create test payload
      addLog('info', '📦 Creating test payment intent payload', 'PAYLOAD');
      
      const testPayload = {
        id: `evt_test_${Date.now()}`,
        object: 'event',
        api_version: '2023-10-16',
        created: Math.floor(Date.now() / 1000),
        data: {
          object: {
            id: `pi_test_${Date.now()}`,
            object: 'payment_intent',
            amount: 50000, // $500.00
            currency: 'usd',
            status: 'succeeded',
            metadata: {
              userId: `test_user_${Date.now()}`,
              serviceType: 'market-intelligence',
              serviceName: 'Market Intelligence Reports',
              userEmail: testEmail,
              userName: 'Test User - Harness'
            },
            receipt_email: testEmail
          }
        },
        livemode: false,
        pending_webhooks: 1,
        request: { id: null, idempotency_key: null },
        type: 'payment_intent.succeeded'
      };

      // Step 2: Generate real Stripe signature
      addLog('info', '🔐 Generating real Stripe webhook signature', 'SIGNATURE');
      const payloadString = JSON.stringify(testPayload);
      
      addLog('info', `📝 Payload preview: ${payloadString.substring(0, 100)}...`, 'SIGNATURE');
      
      const stripeSignature = await generateWebhookSignature(payloadString, webhookSecret);
      addLog('success', `✅ Generated signature: ${stripeSignature.substring(0, 50)}...`, 'SIGNATURE');
      
      addLog('info', `📤 Sending webhook to https://payments.stratiqx.ai/webhook`, 'SEND');
      addLog('info', `Payload size: ${payloadString.length} characters`, 'SEND');

      // Step 3: Send webhook request with real signature
      const response = await fetch('https://payments.stratiqx.ai/webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'stripe-signature': stripeSignature,
          'User-Agent': 'TestHarness/1.0 (Stripe Compatible)',
        },
        body: payloadString,
      });

      const responseData = await response.text();
      
      addLog('info', `📥 Response received: ${response.status}`, 'RESPONSE');
      
      if (response.status === 200) {
        addLog('success', '✅ Webhook accepted!', 'SUCCESS');
        try {
          const jsonData = JSON.parse(responseData);
          setTestResult(jsonData);
          addLog('info', `Event processed: ${jsonData.eventType || 'unknown'}`, 'SUCCESS');
        } catch (e) {
          setTestResult({ rawResponse: responseData });
        }
      } else {
        addLog('error', `❌ Webhook rejected: ${response.status}`, 'ERROR');
        addLog('error', `Response: ${responseData}`, 'ERROR');
        setTestResult({ error: responseData, status: response.status });
        
        // If signature error, provide guidance
        if (responseData.includes('signature')) {
          addLog('warning', '🔍 This is likely a signature validation issue', 'GUIDANCE');
          addLog('warning', 'Check that STRIPE_WEBHOOK_SECRET matches Stripe Dashboard', 'GUIDANCE');
        }
      }

      // Step 4: Check for email logs
      addLog('info', '📧 Now check the worker logs for email processing...', 'EMAIL_CHECK');
      addLog('info', 'Look for: [EMAIL_SEND], [RESEND], or email-related errors', 'EMAIL_CHECK');
      
    } catch (error: any) {
      addLog('error', `💥 Test failed: ${error.message}`, 'ERROR');
      setTestResult({ error: error.message });
    }
    
    setIsTestRunning(false);
    addLog('info', '🏁 Test completed', 'COMPLETE');
  };

  const testDirectEmail = async () => {
    addLog('info', '📧 Testing direct email endpoint...', 'EMAIL_TEST');
    
    try {
      const response = await fetch('https://payments.stratiqx.ai/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: testEmail
        }),
      });

      const result = await response.json();
      
      if (response.ok) {
        addLog('success', '✅ Test email endpoint succeeded', 'EMAIL_TEST');
        addLog('info', `Response: ${JSON.stringify(result)}`, 'EMAIL_TEST');
      } else {
        addLog('error', `❌ Test email failed: ${response.status}`, 'EMAIL_TEST');
        addLog('error', `Error: ${JSON.stringify(result)}`, 'EMAIL_TEST');
      }
    } catch (error: any) {
      addLog('error', `💥 Email test failed: ${error.message}`, 'EMAIL_TEST');
    }
  };

  const testAuthService = async () => {
    addLog('info', '👤 Testing auth service user lookup...', 'AUTH_TEST');
    
    if (!testUserId.trim()) {
      addLog('error', '❌ Please enter a user ID to test', 'AUTH_TEST');
      return;
    }
    
    try {
      // Replicate exact getUserData implementation from webhook worker
      const authUrl = `https://auth.stratiqx.ai/auth/user/${testUserId.trim()}`;
      addLog('info', `🔍 Calling auth service: ${authUrl}`, 'AUTH_TEST');
      
      const authResponse = await fetch(authUrl, {
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json'
          // Note: X-Service header removed due to browser CORS restrictions
          // Webhook worker includes: 'X-Service': 'stratiqx-payment'
        }
      });
      
      addLog('info', `📥 Auth service response: ${authResponse.status} ${authResponse.statusText}`, 'AUTH_TEST');
      
      if (authResponse.ok) {
        const authData = await authResponse.json();
        addLog('success', '✅ Auth service lookup succeeded!', 'AUTH_TEST');
        addLog('info', `👤 User data: ${JSON.stringify(authData, null, 2)}`, 'AUTH_TEST');
        
        // Extract user info following webhook logic
        const userData = {
          email: authData.email,
          fullName: authData.fullName || authData.full_name || authData.name || authData.email?.split('@')[0]
        };
        
        addLog('success', `📧 Extracted email: ${userData.email}`, 'AUTH_TEST');
        addLog('success', `👤 Extracted name: ${userData.fullName}`, 'AUTH_TEST');
        
        setAuthTestResult({
          success: true,
          userData,
          rawResponse: authData
        });
      } else {
        const errorText = await authResponse.text();
        addLog('error', `❌ Auth service failed: ${authResponse.status}`, 'AUTH_TEST');
        addLog('error', `Error response: ${errorText}`, 'AUTH_TEST');
        
        if (authResponse.status === 404) {
          addLog('warning', '🔍 User not found in auth service', 'AUTH_TEST');
          addLog('warning', 'Check if this user ID exists in the onboarding system', 'AUTH_TEST');
        } else if (authResponse.status === 401) {
          addLog('warning', '🔒 Authentication failed - likely missing X-Service header', 'AUTH_TEST');
          addLog('warning', 'Browser CORS prevents sending X-Service header, but webhook worker can', 'AUTH_TEST');
          addLog('info', '✅ This means auth service requires proper service authentication (good!)', 'AUTH_TEST');
        }
        
        setAuthTestResult({
          success: false,
          error: errorText,
          status: authResponse.status
        });
      }
    } catch (error: any) {
      addLog('error', `💥 Auth service test failed: ${error.message}`, 'AUTH_TEST');
      setAuthTestResult({
        success: false,
        error: error.message
      });
    }
  };

  const getLogStyle = (level: LogEntry['level']) => {
    const baseStyle = 'text-sm font-mono p-2 border-l-4 mb-2';
    switch (level) {
      case 'success': return `${baseStyle} border-green-500 bg-green-50 text-green-800`;
      case 'error': return `${baseStyle} border-red-500 bg-red-50 text-red-800`;
      case 'warning': return `${baseStyle} border-yellow-500 bg-yellow-50 text-yellow-800`;
      default: return `${baseStyle} border-blue-500 bg-blue-50 text-blue-800`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          🧪 Webhook Test Harness
        </h1>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-green-600 text-xl">✅</span>
            <div>
              <h3 className="font-semibold text-green-800 mb-1">No CORS Issues!</h3>
              <p className="text-green-700 text-sm">
                This test harness is running on the same domain (preview.stratiqx.ai) as the payment API, 
                eliminating any CORS restrictions for comprehensive testing.
              </p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Test Controls</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Webhook Secret</h3>
                <div className="mb-4">
                  <input
                    type="password"
                    placeholder="Enter webhook secret (whsec_...)"
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Get this from Stripe Dashboard → Webhooks → Your webhook endpoint → Signing secret
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Test Email Address</h3>
                <div className="mb-4">
                  <input
                    type="email"
                    placeholder="Enter test email address"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Email address to test with. Will be used in payment metadata and for email delivery.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Test User ID (Auth Service)</h3>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Enter user ID to test auth service lookup"
                    value={testUserId}
                    onChange={(e) => setTestUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a real user ID from the onboarding system to test auth service connectivity.
                    Example: Try "11" (from real payment logs) or your own user ID.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Webhook Tests</h3>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={testWebhook}
                    disabled={isTestRunning}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isTestRunning ? '⏳ Testing...' : '🧪 Test Webhook'}
                  </Button>
                  
                  <Button
                    onClick={testDirectEmail}
                    disabled={isTestRunning}
                    variant="outline"
                  >
                    📧 Test Email Directly
                  </Button>
                  
                  <Button
                    onClick={testAuthService}
                    disabled={isTestRunning || !testUserId.trim()}
                    variant="outline"
                    className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                  >
                    👤 Test Auth Service
                  </Button>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Log Controls</h3>
                <Button
                  onClick={clearLogs}
                  variant="outline"
                  className="text-gray-600"
                >
                  🗑️ Clear Logs
                </Button>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <h3 className="font-medium text-yellow-800 mb-2">📋 Instructions</h3>
                <ol className="text-sm text-yellow-700 space-y-1">
                  <li>1. Get webhook secret from <a href="https://dashboard.stripe.com/test/webhooks" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Stripe Dashboard</a></li>
                  <li>2. Enter the webhook secret above (starts with whsec_)</li>
                  <li>3. Click "Test Webhook" to run with real signature validation</li>
                  <li>4. Watch detailed logs to see exactly where emails fail</li>
                </ol>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <h3 className="font-medium text-blue-800 mb-2">🔍 What to Look For</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• ✅ Steps 1-7 completing</li>
                  <li>• 📧 [EMAIL_SEND] messages</li>
                  <li>• 📬 [SERVICE_EMAIL] calls</li>
                  <li>• 📤 [RESEND] API calls</li>
                  <li>• ❌ Any failure points</li>
                </ul>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded p-4">
                <h3 className="font-medium text-orange-800 mb-2">⚠️ Known Issue Found</h3>
                <p className="text-sm text-orange-700 mb-2">
                  The webhook processes successfully, but email delivery fails because:
                </p>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Test users don't exist in the auth service (404 error)</li>
                  <li>• Even real user ID "11" from actual payments fails with 404</li>
                  <li>• The system tries to lookup user data before sending emails</li>
                  <li>• Direct email test works fine (bypasses user lookup)</li>
                </ul>
                <p className="text-sm text-orange-700 mt-2">
                  <strong>Next Step:</strong> Use the "Test Auth Service" button to debug user lookup with real user IDs.
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded p-4">
                <h3 className="font-medium text-purple-800 mb-2">🔍 Auth Service Testing</h3>
                <p className="text-sm text-purple-700 mb-2">
                  Test the exact same auth service lookup used by the webhook worker:
                </p>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Enter a real user ID (try "11" from logs, or your own)</li>
                  <li>• Click "Test Auth Service" to check user lookup</li>
                  <li>• See detailed logs of auth service call</li>
                  <li>• Verify user email and name extraction</li>
                </ul>
                <p className="text-xs text-purple-600 mt-2">
                  ⚠️ Browser CORS limitation: Cannot send X-Service header from browser. 
                  The webhook worker doesn't have this limitation.
                </p>
              </div>
            </div>
          </Card>

          {/* Test Results */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Webhook Test Result</h2>
              
              {testResult ? (
                <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-60">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              ) : (
                <p className="text-gray-500">No webhook test result yet</p>
              )}
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Auth Service Test Result</h2>
              
              {authTestResult ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded ${authTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <h3 className={`font-medium mb-2 ${authTestResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {authTestResult.success ? '✅ Auth Service Success' : '❌ Auth Service Failed'}
                    </h3>
                    {authTestResult.success ? (
                      <div className="text-sm text-green-700">
                        <p><strong>Email:</strong> {authTestResult.userData?.email}</p>
                        <p><strong>Full Name:</strong> {authTestResult.userData?.fullName}</p>
                      </div>
                    ) : (
                      <div className="text-sm text-red-700">
                        <p><strong>Status:</strong> {authTestResult.status}</p>
                        <p><strong>Error:</strong> {authTestResult.error}</p>
                      </div>
                    )}
                  </div>
                  
                  <details className="bg-gray-50 p-4 rounded">
                    <summary className="font-medium cursor-pointer">Raw Response</summary>
                    <pre className="text-xs mt-2 overflow-auto">
                      {JSON.stringify(authTestResult, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : (
                <p className="text-gray-500">No auth service test result yet</p>
              )}
            </Card>
          </div>
        </div>

        {/* Live Logs */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Live Test Logs</h2>
            <span className="text-sm text-gray-500">
              {logs.length} entries
            </span>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No logs yet. Run a test to see detailed logging.
              </p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className={getLogStyle(log.level)}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-gray-500 mt-0.5">
                      {log.timestamp}
                    </span>
                    {log.step && (
                      <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                        {log.step}
                      </span>
                    )}
                  </div>
                  <div className="mt-1">{log.message}</div>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </Card>

        {/* External Monitoring */}
        <Card className="mt-6 p-6">
          <h2 className="text-xl font-semibold mb-4">📊 External Monitoring</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-medium text-blue-800 mb-2">Cloudflare Logs</h3>
              <p className="text-sm text-blue-600 mb-2">
                Monitor real-time worker logs:
              </p>
              <code className="text-xs bg-white px-2 py-1 rounded border">
                wrangler tail stratiqx-payment-prod --format pretty
              </code>
            </div>
            
            <div className="bg-green-50 p-4 rounded">
              <h3 className="font-medium text-green-800 mb-2">Stripe Dashboard</h3>
              <p className="text-sm text-green-600 mb-2">
                Check webhook delivery status:
              </p>
              <a 
                href="https://dashboard.stripe.com/test/webhooks" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-blue-600 underline"
              >
                Open Stripe Webhooks →
              </a>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};